import React, { createContext, useContext, useState, useCallback } from 'react';
import { EXECUTOR_API } from '../types';
import { setCurrentUserEmail, setActiveTenant as storeActiveTenant } from '../utils/userStore';

export type UserRole = 'admin' | 'editor' | 'viewer';

export interface TenantOption {
  slug: string;
  name: string;
}

interface UserState {
  email: string | null;
  role: UserRole;
  loading: boolean;
  isSuperadmin: boolean;
  tenants: TenantOption[];
  activeTenant: string | null;
}

interface UserContextValue extends UserState {
  canEdit: boolean;
  isAdmin: boolean;
  login: (credential: string) => Promise<{ ok: boolean; error?: string }>;
  loginAsDev: (email: string, role: UserRole) => void;
  logout: () => void;
  refresh: () => void;
  /** Cambia el tenant activo (solo superadmin). Recarga la app para re-scopear. */
  selectTenant: (slug: string | null) => void;
}

const UserContext = createContext<UserContextValue>({
  email: null,
  role: 'viewer',
  loading: false,
  isSuperadmin: false,
  tenants: [],
  activeTenant: null,
  canEdit: false,
  isAdmin: false,
  login: async () => ({ ok: false }),
  loginAsDev: () => {},
  logout: () => {},
  refresh: () => {},
  selectTenant: () => {},
});

/** Decode the JWT payload from a Google credential token (client-side only). */
function decodeGoogleJwt(token: string): { email: string; hd?: string } {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(json);
}

const ALLOWED_DOMAIN = 'cubicup.com';
const SESSION_KEY = 'cubic_user_email';
const TENANT_KEY = 'cubic_active_tenant';

/** Persiste el tenant activo (localStorage + store del cliente API). */
function persistTenant(slug: string | null): void {
  if (slug) localStorage.setItem(TENANT_KEY, slug);
  else localStorage.removeItem(TENANT_KEY);
  storeActiveTenant(slug);
}

interface MeResponse {
  email: string;
  role: UserRole;
  is_superadmin?: boolean;
  tenants?: TenantOption[];
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const savedEmail = localStorage.getItem(SESSION_KEY);
  const savedTenant = localStorage.getItem(TENANT_KEY);
  // Restaura el tenant activo en el store del cliente API antes de cualquier fetch.
  storeActiveTenant(savedTenant);

  const [state, setState] = useState<UserState>({
    email: savedEmail,
    role: 'viewer',
    loading: !!savedEmail,
    isSuperadmin: false,
    tenants: [],
    activeTenant: savedTenant,
  });

  /** Fetch identity + tenant info from executor and update state. */
  const fetchRole = useCallback(async (email: string) => {
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/users/me`, {
        headers: { 'X-Goog-Authenticated-User-Email': `accounts.google.com:${email}` },
      });
      if (res.ok) {
        const me = (await res.json()) as MeResponse;
        const tenants = me.tenants ?? [];
        setState((prev) => {
          // Si el tenant activo ya no es válido para este usuario, se descarta.
          const validTenant =
            prev.activeTenant && tenants.some((t) => t.slug === prev.activeTenant)
              ? prev.activeTenant
              : null;
          if (validTenant !== prev.activeTenant) persistTenant(validTenant);
          return {
            email: me.email,
            role: me.role,
            loading: false,
            isSuperadmin: me.is_superadmin ?? false,
            tenants,
            activeTenant: validTenant,
          };
        });
      } else {
        setState((prev) => ({ ...prev, email, role: 'viewer', loading: false }));
      }
    } catch {
      setState((prev) => ({ ...prev, email, role: 'viewer', loading: false }));
    }
  }, []);

  React.useEffect(() => {
    if (savedEmail) {
      setCurrentUserEmail(savedEmail);
      fetchRole(savedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (credential: string): Promise<{ ok: boolean; error?: string }> => {
    let payload: { email: string; hd?: string };
    try {
      payload = decodeGoogleJwt(credential);
    } catch {
      return { ok: false, error: 'Token inválido.' };
    }

    const email = payload.email?.toLowerCase();
    if (!email || !email.endsWith(`@${ALLOWED_DOMAIN}`)) {
      return { ok: false, error: `Solo cuentas @${ALLOWED_DOMAIN} pueden acceder.` };
    }

    localStorage.setItem(SESSION_KEY, email);
    setCurrentUserEmail(email);
    setState((prev) => ({ ...prev, email, role: 'viewer', loading: true }));
    await fetchRole(email);
    return { ok: true };
  }, [fetchRole]);

  const loginAsDev = useCallback((email: string, role: UserRole) => {
    localStorage.setItem(SESSION_KEY, email);
    setCurrentUserEmail(email);
    setState((prev) => ({ ...prev, email, role, loading: false }));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    persistTenant(null);
    setCurrentUserEmail(null);
    setState({
      email: null, role: 'viewer', loading: false,
      isSuperadmin: false, tenants: [], activeTenant: null,
    });
  }, []);

  const refresh = useCallback(() => {
    if (state.email) fetchRole(state.email);
  }, [state.email, fetchRole]);

  const selectTenant = useCallback((slug: string | null) => {
    persistTenant(slug);
    // Recargar re-scopea todos los listados con el nuevo X-Tenant (sencillo y
    // seguro frente a rastrear qué vistas dependen del tenant).
    window.location.reload();
  }, []);

  const value: UserContextValue = {
    ...state,
    canEdit: state.role === 'admin' || state.role === 'editor',
    isAdmin: state.role === 'admin',
    login,
    loginAsDev,
    logout,
    refresh,
    selectTenant,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextValue => useContext(UserContext);
